/**
 * utils/pushClient.js — Iter-181 Messaging Slice 3.
 *
 * Web Push (VAPID) subscribe / unsubscribe + permission UX.
 *
 * Usage:
 *   import { pushClient } from '../utils/pushClient';
 *   await pushClient.enable(); // requests permission + subscribes
 *   await pushClient.disable();
 *   await pushClient.isEnabled();
 */
import api from './api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

class PushClient {
  constructor() {
    this.publicKey = null;
    this.available = null;
  }

  async _registration() {
    if (!('serviceWorker' in navigator)) return null;
    return navigator.serviceWorker.ready;
  }

  isSupported() {
    return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  permission() {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission; // 'default' | 'granted' | 'denied'
  }

  async _fetchPublicKey() {
    if (this.publicKey) return this.publicKey;
    const { data } = await api.get('/push/vapid-public-key');
    this.publicKey = data?.public_key || null;
    this.available = !!data?.available;
    return this.publicKey;
  }

  async isEnabled() {
    if (!this.isSupported()) return false;
    const reg = await this._registration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  }

  async enable() {
    if (!this.isSupported()) throw new Error('Push notifications are not supported in this browser');
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') throw new Error('Permission denied');

    const publicKey = await this._fetchPublicKey();
    if (!publicKey) throw new Error('Push not configured on server');

    const reg = await this._registration();
    if (!reg) throw new Error('Service worker not ready');

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    const json = sub.toJSON();
    await api.post('/push/subscribe', {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });
    return true;
  }

  async disable() {
    const reg = await this._registration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return false;
    try {
      await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
    } catch (_) { /* server error tolerated */ }
    await sub.unsubscribe();
    return true;
  }

  async sendTest() {
    return api.post('/push/test');
  }

  async getPreferences() {
    const { data } = await api.get('/push/preferences');
    return data;
  }

  async updatePreferences(prefs) {
    await api.put('/push/preferences', prefs);
  }
}

export const pushClient = new PushClient();

// Listen for SW message asking us to re-subscribe (after pushsubscriptionchange).
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', async (event) => {
    if (event.data?.type === 'PUSH_RESUBSCRIBE_NEEDED') {
      try { await pushClient.enable(); } catch (_) { /* noop */ }
    }
  });
}
